import AttachmentsRow from '@features/logs/components/incidents/AttachmentsRow'
import { getIncidentReportSeverityLabel } from '@features/logs/utils/incidentSeverityUtils'
import { Badge, type BadgeColour } from '@lmc/design-system'
import { type Home, IncidentSeverity, IncidentStatus } from '@lmc/ts-common'
import GeoLocation from 'components/GeoLocation/GeoLocation'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import { ReadGroup, ReadItem, ReadWide } from '../readLayout'
import {
  type CaseMetadata,
  closure,
  handoverKey,
  STATUS_LABEL_KEYS
} from '../readRules'
import { formatDate, formatTime, incidentAttachments } from '../readValues'

const STATUS_COLOURS: Record<IncidentStatus, BadgeColour> = {
  [IncidentStatus.closed]: 'grey',
  [IncidentStatus.needsReview]: 'red',
  [IncidentStatus.open]: 'purple'
}

/** Rising with severity; the design shows "Significant" in volcano. */
const SEVERITY_COLOURS: Record<IncidentSeverity, BadgeColour> = {
  [IncidentSeverity.level1]: 'green',
  [IncidentSeverity.level2]: 'orange',
  [IncidentSeverity.level3]: 'volcano',
  [IncidentSeverity.level4]: 'red',
  [IncidentSeverity.level5]: 'purple'
}

/**
 * Step 1, read only: the case at a glance, then the incident's time and
 * details and the log's own details in two groups the reader can fold away.
 * The page shows no log card for a v2 case, so every log detail is here.
 */
const IncidentInformationReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { log, severity, status } = incidentReport
  const { caseOwner, caseReference, incidentOccurredAt } =
    incidentReport as typeof incidentReport & CaseMetadata
  const attachments = incidentAttachments(incidentReport)
  const closed = closure(incidentReport)
  const handover = handoverKey(log.isNextHandover)
  // By presence: 0 is a real coordinate.
  const showsGps = Boolean(
    home?.features?.geoLocation?.enabled &&
      log.latitude != null &&
      log.longitude != null
  )

  return (
    <>
      {/* The owner sits beside the reference, as designed. */}
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_INFO_REFERENCE_ID)}
        value={caseReference}
        testId="incident-case-v2-view-reference"
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_INFO_CASE_OWNER)}
        value={caseOwner?.name}
        testId="incident-case-v2-view-case-owner"
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_VIEW_STATUS)}
        value={
          <Badge colour={STATUS_COLOURS[status]}>
            {t(STATUS_LABEL_KEYS[status])}
          </Badge>
        }
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_INFO_INCIDENT_SEVERITY)}
        value={
          severity ? (
            <Badge colour={SEVERITY_COLOURS[severity]}>
              {getIncidentReportSeverityLabel(
                severity,
                home?.incidentSeverity,
                t
              )}
            </Badge>
          ) : null
        }
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_INFO_INCIDENT_TYPE)}
        value={log.item?.name}
      />
      <ReadWide>
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_INFO_TITLE)}
          value={log.title}
        />
      </ReadWide>
      {closed && (
        <>
          <ReadItem
            label={t(incidentReportsConstants.CLOSED_BY)}
            value={closed.closedBy}
          />
          <ReadWide>
            <ReadItem
              label={t(incidentReportsConstants.CLOSE_COMMENT)}
              value={closed.closeComment}
            />
          </ReadWide>
        </>
      )}

      <ReadGroup
        title={t(
          incidentReportsConstants.CASE_V2_INFO_INCIDENT_TIME_AND_DETAILS
        )}
        testId="incident-case-v2-view-incident-time"
      >
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_VIEW_INCIDENT_DATE)}
          value={formatDate(incidentOccurredAt)}
        />
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_VIEW_INCIDENT_TIME)}
          value={formatTime(incidentOccurredAt)}
        />
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_VIEW_INCIDENT_LOCATION)}
            value={log.location}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_VIEW_INCIDENT_DESCRIPTION
            )}
            value={log.description}
          />
        </ReadWide>
      </ReadGroup>

      <ReadGroup
        title={t(incidentReportsConstants.CASE_V2_VIEW_LOG_DETAILS)}
        testId="incident-case-v2-view-log-details"
      >
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_VIEW_LOGGED_DATE)}
          value={formatDate(log.timeLogged)}
        />
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_VIEW_LOGGED_TIME)}
          value={formatTime(log.timeLogged)}
        />
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_INFO_LOGGED_BY)}
            value={log.loggedBy?.name}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.WITNESSED_BY)}
            value={log.witnessedBy?.name}
          />
        </ReadWide>
        {attachments.length > 0 && (
          <ReadWide>
            <ReadItem
              label={t(incidentReportsConstants.ATTACHMENTS)}
              value={
                <AttachmentsRow
                  attachments={attachments}
                  testId="incident-case-v2-view-attachments"
                />
              }
            />
          </ReadWide>
        )}
        {showsGps && (
          <ReadWide>
            <ReadItem
              label={t(incidentReportsConstants.GPS_LOCATION)}
              value={
                <GeoLocation
                  longitude={log.longitude}
                  latitude={log.latitude}
                  accuracy={log.accuracy}
                />
              }
            />
          </ReadWide>
        )}
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_INFO_LINKED_GOALS)}
            value={log.goals?.map((goal) => goal.title)}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_INFO_HANDOVER)}
            value={handover && t(handover)}
          />
        </ReadWide>
      </ReadGroup>
    </>
  )
}

export default IncidentInformationReadPanel
